import { OpenBookmarkPossibilitiesProvider } from "client_legacy/Documents/Bookmarks/bookmarkOpenPossibilities";
import {
	BookmarkModel,
	RenameBookmarkModel
} from "client/Api/Documents/Bookmarks/bookmarks";
import { Opener, OpenerOptions } from "client_legacy/Documents/Bookmarks/opener";
import { DocumentStoreType, OfficeDocumentEditor } from "client/Api/Documents/common";
import {
	getItemsInPreferredOrder,
	UserEditorPreference
} from "client_legacy/Documents/officeDocumentsOpenPreference";
import { getOpenLocalizationsForExtension } from "client_legacy/Documents/openLocalizations";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import {
	ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
} from "client_legacy/Documents/SharePoint/documentOpenPossibilities";
import {
	ActionAndOthers,
	ActionGroup,
	ActionKind,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { max, min } from "client_legacy/ndaw/arrayHelpers";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	documentsConfiguration
} from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { AlertManager } from "client/Hooks";
import { EmailClient, getEffectiveEmailClient } from "client_legacy/Email/emailClient";
import { openSetOfficePreferenceDialog } from "../SetOfficePreferenceDialog";

export type BookmarkActionContext = {
	selection: readonly BookmarkModel[];
	hasASearchTerm: boolean;
	allPositions: Map<number, number>;
	isMobile: boolean;
	isInSelectMode: boolean;
};

export type BookmarkAction = ActionAndOthers<BookmarkActionContext> & {
	quickActionIndex?: number;
};

const openGroup: ActionGroup = {
	kind: "alternative",
	name: "openGroup",
	title: localize("_Open"),
	icon: "folder-open"
};

const manageGroup: ActionGroup = {
	kind: "virtual",
	name: "manageGroup"
};

const _openBookmarkPossibilitiesProvider = new OpenBookmarkPossibilitiesProvider();

export type RootActionHandlers = {
	enableSelectMode(): void;
	disableSelectMode(): void;
	selectAll(): void;
	deselectAll(): void;
};

export type SelectionActionHandlers = OpenerOptions & {
	onMoveUp(id: number): void;
	onMoveDown(id: number): void;
	onRenameBookmark(
		renameBookmarkModel: RenameBookmarkModel
	): void;
	onRemoveBookmarks(
		ids: number[],
		names: string[]
	): void;
	onDownloadBookmark(selection: BookmarkModel): void;
	onMoveItem(selection: BookmarkModel): void;
	onDeleteItem(selection: BookmarkModel): void;
	onCopyItem(selection: BookmarkModel): void;
	onRenameItem(selection: BookmarkModel): void;
	onPermissionsChange(selection: BookmarkModel): void;
	onInviteUser(selection: BookmarkModel): void;
	onAttachToEmail(selection: BookmarkModel): void;
	onShareLink(selection: BookmarkModel): void;
	onVersionHistoryOpen(selection: BookmarkModel): void;
};

export type ActionHandlers = RootActionHandlers & SelectionActionHandlers;

export function getRootActions(
	itemsOnPage: readonly BookmarkModel[],
	handlers: RootActionHandlers
): readonly BookmarkAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "Select",
			title: localize("_Select"),
			icon: "hand-pointer",
			canExecute: (c) => {
				return c.isMobile
					&& !c.isInSelectMode
					&& itemsOnPage.length > 0;
			},
			execute: () => {
				handlers.enableSelectMode();
			}
		},
		{
			kind: ActionKind.Regular,
			name: "Cancel",
			title: localize("_Cancel"),
			icon: "xmark",
			canExecute: (c) => {
				return c.isMobile && c.isInSelectMode;
			},
			execute: () => {
				handlers.disableSelectMode();
			}
		},
		{
			kind: ActionKind.Regular,
			name: "DeselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: (c) => c.selection.length > 0 && (!c.isMobile || c.isInSelectMode),
			execute: () => handlers.deselectAll()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		},
		{
			kind: ActionKind.Regular,
			name: "SelectAll",
			title: localize("_SelectAll"),
			icon: "circle-check",
			canExecute: (c) => {
				return c.isMobile
					&& c.isInSelectMode
					&& c.selection.length !== itemsOnPage.length;
			},
			execute: () => {
				handlers.selectAll();
			}
		}
	];
}

export function getOpenActions(
	selection: readonly BookmarkModel[],
	handlers: SelectionActionHandlers,
	alertManager: AlertManager,
	userPreference: UserEditorPreference
): readonly BookmarkAction[] {
	const {
		onOpenFolderRequestStart,
		onOpenFolderRequestEnd,
		onOpenFileRequestStart,
		onOpenFileRequestEnd,
		onChangeNavigationState
	} = handlers;

	const opener: Opener = new Opener(
		{
			onOpenFolderRequestStart,
			onOpenFolderRequestEnd,
			onOpenFileRequestStart,
			onOpenFileRequestEnd,
			onChangeNavigationState
		},
		alertManager
	);

	return [
		{
			kind: ActionKind.Regular,
			name: "OpenFolder",
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;
				if (selection.length !== 1) {
					return false;
				}

				return _openBookmarkPossibilitiesProvider.get(selection[0]).folder;
			},
			execute: (context) => {
				const bookmark: BookmarkModel = context.selection[0];
				opener.openFolderFromDocumentsPage(bookmark);
			}
		},
		...getOpenInEditorActionsOrderedByPreference(
			selection,
			opener,
			userPreference
		),
		{
			kind: ActionKind.Regular,
			name: "OpenInOnlineViewer",
			group: openGroup,
			title: localize("_OpenInAppName")
				.replace(
					"{0}",
					localize("_OnlineViewer")
				),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;

				if (selection.length !== 1) {
					return false;
				}

				const item: BookmarkModel = selection[0];
				if (item.itemType !== DocumentItemType.File
						|| item.storeType !== DocumentStoreType.SharePoint
				) {
					return false;
				}

				const { sharePointViewer } = _openBookmarkPossibilitiesProvider.get(selection[0]);

				return sharePointViewer;
			},
			execute: (c) => {
				const item: BookmarkModel = c.selection[0];

				if (item.itemType !== DocumentItemType.File) {
					throw new Error("The document is not a file.");
				}

				if (item.storeType !== DocumentStoreType.SharePoint) {
					throw new Error("The share point viewer only supports share point files.");
				}

				opener.openFileInSharePointViewer(item);
			}
		},
		{
			kind: ActionKind.Regular,
			name: "OpenInBrowser",
			group: openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;
				if (selection.length !== 1) {
					return false;
				}

				return _openBookmarkPossibilitiesProvider.get(selection[0]).browser;
			},
			execute: (context) => {
				const bookmark: BookmarkModel = context.selection[0];
				opener.openFileInline(bookmark);
			}
		},
		{
			kind: ActionKind.Regular,
			name: "open-url-file",
			group: openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;
				if (selection.length !== 1) {
					return false;
				}

				return _openBookmarkPossibilitiesProvider.get(selection[0]).externalUrl;
			},
			execute: (context) => {
				const bookmark: BookmarkModel = context.selection[0];
				opener.openUrlFile(bookmark);
			}
		},
		{
			kind: ActionKind.Regular,
			name: "OpenInImagePreviewDialog",
			group: openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;
				if (selection.length !== 1) {
					return false;
				}

				return _openBookmarkPossibilitiesProvider.get(selection[0]).imagePreviewDialog;
			},
			execute: (context) => {
				const bookmark: BookmarkModel = context.selection[0];
				opener.openInImagePreviewDialog(bookmark);
			}
		},
		{
			kind: ActionKind.Regular,
			name: "SetEditorPreference",
			group: openGroup,
			title: localize("_SetPreference"),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;
				if (selection.length !== 1) {
					return false;
				}

				const { wopi, local } = _openBookmarkPossibilitiesProvider.get(selection[0]);
				return wopi || local;
			},
			execute: () => {
				openSetOfficePreferenceDialog();
			}
		}
	];
}

export function getSelectionActions(
	selection: readonly BookmarkModel[],
	handlers: SelectionActionHandlers,
	alertManager: AlertManager,
	userPreference: UserEditorPreference
): BookmarkAction[] {
	const {
		onOpenFolderRequestStart,
		onOpenFolderRequestEnd,
		onOpenFileRequestStart,
		onOpenFileRequestEnd,
		onMoveUp,
		onMoveDown,
		onRenameBookmark,
		onRemoveBookmarks,
		onChangeNavigationState,
		onDownloadBookmark,
		onMoveItem,
		onDeleteItem,
		onCopyItem,
		onRenameItem,
		onPermissionsChange,
		onInviteUser,
		onAttachToEmail,
		onShareLink,
		onVersionHistoryOpen
	} = handlers;

	const opener: Opener = new Opener(
		{
			onOpenFolderRequestStart,
			onOpenFolderRequestEnd,
			onOpenFileRequestStart,
			onOpenFileRequestEnd,
			onChangeNavigationState
		},
		alertManager
	);

	return [
		...getOpenActions(
			selection,
			handlers,
			alertManager,
			userPreference
		),
		{
			kind: ActionKind.Regular,
			name: "OpenLocation",
			title: localize("_OpenLocation"),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;
				if (selection.length !== 1) {
					return false;
				}

				return _openBookmarkPossibilitiesProvider.get(selection[0]).location;
			},
			execute: (context) => {
				const bookmark: BookmarkModel = context.selection[0];
				opener.openLocation(bookmark);
			}
		},
		{
			kind: ActionKind.Regular,
			name: "shareFile",
			title: localize("_ShareLink"),
			canExecute: (context) => {
				const { selection } = context;
				const { isSharingViaLinkEnabled } = documentsConfiguration().sharePoint;

				return selection.length === 1
					&& selection[0].storeType === DocumentStoreType.SharePoint
					&& isSharingViaLinkEnabled
					&& (
						selection[0].itemType === DocumentItemType.File
						|| selection[0].itemType === DocumentItemType.OneNoteNotebook
					);
			},
			execute: (context) => onShareLink(context.selection[0]),
			icon: "link",
			quickActionIndex: 0
		},
		{
			kind: ActionKind.Regular,
			name: "inviteUser",
			title: localize("_InviteUser"),
			canExecute: (context) => {
				const { selection } = context;

				return selection.length === 1
					&& selection[0].storeType === DocumentStoreType.SharePoint;
			},
			execute: (context) => onInviteUser(context.selection[0]),
			icon: "user-plus"
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			canExecute: (context) => context.selection.length === 1,
			execute: (context) => onDeleteItem(context.selection[0]),
			icon: "trash-can",
			group: manageGroup
		},
		{
			kind: ActionKind.Regular,
			name: "rename",
			title: localize("_Rename"),
			canExecute: (context) => context.selection.length === 1,
			execute: (context) => onRenameItem(context.selection[0]),
			icon: "pen",
			group: manageGroup
		},
		{
			kind: ActionKind.Regular,
			name: "move",
			title: localize("_MoveTo"),
			canExecute: (context) => context.selection.length === 1,
			execute: (context) => onMoveItem(context.selection[0]),
			icon: "arrows-up-down-left-right",
			group: manageGroup
		},
		{
			kind: ActionKind.Regular,
			name: "copy",
			title: localize("_CopyTo"),
			canExecute: (context) => {
				const { selection } = context;

				const { allowCrossStoreOperations } = documentsConfiguration();

				return selection.length === 1
					&& (
						allowCrossStoreOperations
						|| selection[0].itemType === DocumentItemType.File
					);
			},
			execute: (context) => onCopyItem(context.selection[0]),
			icon: "copy",
			group: manageGroup
		},
		{
			kind: ActionKind.Regular,
			name: "Download",
			title: localize("_Download"),
			canExecute: (context) => {
				const { selection } = context;

				return selection.length === 1
					&& selection[0].itemType === DocumentItemType.File;
			},
			execute: (context) => onDownloadBookmark(context.selection[0]),
			icon: "download"
		},
		{
			kind: ActionKind.Regular,
			name: "remove",
			title: localize("_RemoveFromBookmark"),
			icon: "star",
			canExecute: (context) => context.selection.length > 0,
			execute: (context) => {
				const { selection } = context;
				const bookmarkIds: number[] = selection.map((x) => x.id);
				const bookmarkNames: string[] = selection.map(x => "'" + x.name + "'");
				onRemoveBookmarks(bookmarkIds, bookmarkNames);
			},
			quickActionIndex: 1
		},
		{
			kind: ActionKind.Regular,
			name: "renameBookmark",
			title: localize("_RenameBookmark"),
			icon: "pen",
			canExecute: (context) => context.selection.length === 1,
			execute: (context) => {
				const {
					id,
					name
				} = context.selection[0];

				onRenameBookmark({
					id,
					name
				});
			}
		},
		{
			kind: ActionKind.Regular,
			name: "versions",
			title: localize("_VersionHistory"),
			canExecute: (context) => {
				const { selection } = context;

				return selection.length === 1
					&& selection[0].storeType === DocumentStoreType.SharePoint
					&& selection[0].itemType === DocumentItemType.File;
			},
			execute: (context) => onVersionHistoryOpen(context.selection[0]),
			icon: "clock-rotate-left"
		},
		{
			kind: ActionKind.Regular,
			name: "attachToEmail",
			title: localize("_AttachToEmail"),
			canExecute: (context) => {
				const { selection } = context;
				const emailClient: EmailClient = getEffectiveEmailClient();

				return emailClient.supportsEditDraft
					&& selection.length === 1
					&& selection[0].itemType === DocumentItemType.File; // folder or OneNote are not supported
			},
			execute: (context) => onAttachToEmail(context.selection[0]),
			icon: "envelope"
		},
		{
			kind: ActionKind.Regular,
			name: "shareSettings",
			title: localize("_Permissions"),
			canExecute: (context) => {
				const { selection } = context;

				return selection.length === 1
					&& selection[0].storeType === DocumentStoreType.SharePoint;
			},
			execute: (context) => onPermissionsChange(context.selection[0]),
			icon: "key"
		},
		{
			kind: ActionKind.Regular,
			name: "MoveUp",
			title: localize("_MoveUp"),
			icon: "arrow-up",
			canExecute: (context) => {
				const { selection } = context;
				if (selection.length !== 1) {
					return false;
				}

				if (context.hasASearchTerm) {
					return false;
				}

				const position: number | undefined = context.allPositions.get(selection[0].id);
				const isFirst: boolean = position === min([...context.allPositions.values()]);
				return !isFirst;
			},
			execute: (context) => {
				onMoveUp(context.selection[0].id);
			},
			quickActionIndex: 2
		},
		{
			kind: ActionKind.Regular,
			name: "MoveDown",
			title: localize("_MoveDown"),
			icon: "arrow-down",
			canExecute: (context) => {
				const { selection } = context;
				if (selection.length !== 1) {
					return false;
				}

				if (context.hasASearchTerm) {
					return false;
				}

				const position: number | undefined = context.allPositions.get(selection[0].id);
				const isLast: boolean = position === max([...context.allPositions.values()]);
				return !isLast;
			},
			execute: (context) => {
				onMoveDown(context.selection[0].id);
			},
			quickActionIndex: 3
		}
	];
}

function getOpenInEditorActionsOrderedByPreference(
	selection: readonly BookmarkModel[],
	opener: Opener,
	userPreference: UserEditorPreference
): BookmarkAction[] {

	const defaultEditor: OfficeDocumentEditor = getDefaultEnvironmentOfficeDocumentEditor(selection);

	return getItemsInPreferredOrder<BookmarkAction>(
		defaultEditor,
		[
			[
				OfficeDocumentEditor.Online,
				() => ({
					kind: ActionKind.Regular,
					name: "OpenInOnlineEditor",
					group: openGroup,
					title: (c) => {
						const { selection } = c;
						if (selection.length !== 1
							|| selection[0].itemType === DocumentItemType.Folder
						) {
							return "";
						}

						const bookmark: BookmarkModel = selection[0];

						const extension: string = bookmark.itemType === DocumentItemType.OneNoteNotebook
							? ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
							: bookmark.extension;

						return getOpenLocalizationsForExtension(extension)
							.online
							.action;
					},
					icon: "folder-open",
					canExecute: (context) => {
						const { selection } = context;
						if (selection.length !== 1) {
							return false;
						}

						return _openBookmarkPossibilitiesProvider.get(selection[0]).wopi;
					},
					execute: (context) => {
						const bookmark: BookmarkModel = context.selection[0];
						opener.openFileInWopi(bookmark);
					}
				})
			],
			[
				OfficeDocumentEditor.Local,
				() => ({
					kind: ActionKind.Regular,
					name: "OpenInLocalEditor",
					group: openGroup,
					title: (c) => {
						const { selection } = c;
						if (selection.length !== 1
							|| selection[0].itemType === DocumentItemType.Folder
						) {
							return "";
						}

						const bookmark: BookmarkModel = selection[0];

						const extension: string = bookmark.itemType === DocumentItemType.OneNoteNotebook
							? ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
							: bookmark.extension;

						return getOpenLocalizationsForExtension(extension)
							.local
							.action;
					},
					icon: "folder-open",
					canExecute: (context) => {
						const { selection } = context;
						if (selection.length !== 1) {
							return false;
						}

						return _openBookmarkPossibilitiesProvider.get(selection[0]).local;
					},
					execute: (context) => {
						const bookmark: BookmarkModel = context.selection[0];
						opener.openFileLocal(bookmark, _openBookmarkPossibilitiesProvider.get(bookmark).wopi);
					}
				})
			]
		],
		userPreference
	);
}

function getDefaultEnvironmentOfficeDocumentEditor(bookmarks: readonly BookmarkModel[]): OfficeDocumentEditor {
	const { sharePoint, webDav } = documentsConfiguration();

	if (bookmarks.length > 0) {
		const storeType: DocumentStoreType.SharePoint | DocumentStoreType.WebDav = bookmarks[0].storeType;
		const areAllFromTheSameStore: boolean = bookmarks.every((x) => x.storeType === storeType);

		if (areAllFromTheSameStore) {
			switch (storeType) {
				case DocumentStoreType.WebDav:
					return webDav
						? webDav.defaultOfficeDocumentEditor
						: OfficeDocumentEditor.Online;
				case DocumentStoreType.SharePoint:
					return sharePoint.defaultOfficeDocumentEditor;
				default:
					never(storeType);
			}
		}
	}

	// Either there is no bookmark or it is a mix of different stores, we cannot determine
	// what the correct default is, so Online it is
	return OfficeDocumentEditor.Online;
}
